import { defineStore } from 'pinia'
import { ref } from 'vue'
import { csvApi } from '@/lib/api'
import { isDemoRejection } from '@/lib/formErrors'
import { importFailureMessage, importMayStillFinish, unconfirmedJob, type DuplicateRow } from '@/lib/importMapping'
import { useNotificationStore } from './notifications'

/** A synchronous import of a 10MB file can outlast axios's 30s default; the server keeps going either way. */
export const IMPORT_REQUEST_TIMEOUT_MS = 5 * 60 * 1000

export interface ImportJob {
  id: string
  entity: 'contacts' | 'companies' | 'deals'
  status: 'pending' | 'processing' | 'completed' | 'failed'
  fileName: string
  progress?: number
  result?: ImportResult
  error?: string
  createdAt: string
  completedAt?: string
}

export interface ImportResult {
  totalRows: number
  inserted: number
  updated: number
  /** Rows that matched an existing record and were left as they are (contacts, companies). */
  skipped?: number
  /** Facebook import: rows the company keyword filter left out. */
  filteredOut?: number
  failed: number
  errors: ImportError[]
  /** Contacts imports: the rows that matched an existing contact and were skipped. */
  duplicates?: DuplicateRow[]
  /** Never sent by the API; kept optional for older callers. */
  summary?: {
    insertedRecords: any[]
    updatedRecords: any[]
    failedRecords: any[]
  }
}

export interface ImportError {
  row: number
  field?: string
  message: string
  data?: any
}

export type EntityType = 'contacts' | 'companies' | 'deals'

export const useCsvStore = defineStore('csv', () => {
  const loading = ref(false)
  const importing = ref(false)
  const importJob = ref<ImportJob | null>(null)
  const error = ref<string | null>(null)
  
  const notifications = useNotificationStore()

  /**
   * Uploads a file to `POST /import/<entity>`. The API imports it synchronously: the job in the
   * response is final (`completed` with its result, or `failed` with `error`), so there is nothing
   * to poll. The import dialog shows the outcome; a request that fails sets `error` (the demo org's
   * write block sets nothing: it already showed its toast). Resolves to the job, or null.
   *
   * When the page loses the answer (its timeout, a proxy's 502/503/504, a dropped connection) the
   * server may still be importing: `importJob` becomes a still-running job without a result
   * (`unconfirmedJob`), never an error inviting a second import of the same rows.
   */
  async function importData(entity: EntityType, file: File): Promise<ImportJob | null> {
    importing.value = true
    error.value = null
    importJob.value = null

    try {
      const response = await csvApi.import(entity, file, { timeout: IMPORT_REQUEST_TIMEOUT_MS })
      importJob.value = response.job ?? null
      return importJob.value
    } catch (err: unknown) {
      if (importMayStillFinish(err)) {
        importJob.value = unconfirmedJob(entity, file.name)
        return importJob.value
      }
      if (!isDemoRejection(err)) error.value = importFailureMessage(err, `The ${entity} import did not go through. Try again.`)
      return null
    } finally {
      importing.value = false
    }
  }

  // Get import job details. NOTE: the API has no `GET /import/jobs/:id` (imports finish inside the
  // POST); kept only so older imports of the store still type-check.
  async function getImportJob(jobId: string): Promise<ImportJob | null> {
    loading.value = true
    error.value = null

    try {
      const job = await csvApi.getImportJob(jobId)
      importJob.value = job
      return job
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to get import job'
      error.value = errorMessage
      notifications.error(errorMessage)
      return null
    } finally {
      loading.value = false
    }
  }

  // Clear current import job
  function clearImportJob(): void {
    importJob.value = null
    error.value = null
  }

  // Get CSV template for entity
  function getTemplate(entity: EntityType): string {
    switch (entity) {
      case 'contacts':
        // emailOptOut stays blank in the sample: a blank cell changes nothing, while an explicit
        // `false` would clear an existing contact's opt-out when a filled-in template is re-imported.
        return 'firstName,lastName,email,phone,jobTitle,description,remarks,emailOptOut,companyName,tags,priority\nJohn,Doe,john.doe@example.com,+1234567890,Software Engineer,Interested in services,Follow up next week,,Acme Corp,prospect,normal'
      
      case 'companies':
        return 'name,domain,industry,size,address,tags\nAcme Corporation,acme.com,Technology,50-100,123 Main St,client,enterprise'
      
      case 'deals':
        return 'name,description,contactEmail,contactPhone,companyName,source,tags\nWebsite Redesign Project,Complete redesign of company website,john.doe@acme.com,+1234567890,Acme Corp,referral,website,project'
      
      default:
        return ''
    }
  }

  // Download CSV template
  function downloadTemplate(entity: EntityType): void {
    const template = getTemplate(entity)
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    
    link.href = url
    link.download = `${entity}_template.csv`
    link.style.display = 'none'
    
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    notifications.success(`${entity} template downloaded`)
  }

  return {
    // State
    loading,
    importing,
    importJob,
    error,
    
    // Actions
    importData,
    getImportJob,
    clearImportJob,
    getTemplate,
    downloadTemplate,
  }
})
